import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import biasCenterImg from "../../../media/images/bias-center.png";
import biasLeaningLeftImg from "../../../media/images/bias-leaning-left.png";
import biasLeaningRightImg from "../../../media/images/bias-leaning-right.png";
import biasLeftImg from "../../../media/images/bias-left.png";
import biasRightImg from "../../../media/images/bias-right.png";
import { colorArray, sortOptions } from "../../../constants";
import MenuPrimary from "../../../components/menu/MenuPrimary";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";

const BarChartWithImages = ({
  labels,
  metricCounts,
  metricFilter,
  sort,
  setSort,
  sortData,
  unratedMetric,
  maxMetricCount,
  sortVal,
  setSortVal,
}) => {
  const d3Container = useRef(null);
  const [containerWidth, setContainerWidth] = useState(800);
  const [svgHeight, setSvgHeight] = useState("45rem");
  const [svgWidth, setSvgWidth] = useState("80rem");
  const [tooltipContent, setTooltipContent] = useState("");
  const [tooltipVisibility, setTooltipVisibility] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipTitle, setTooltipTitle] = useState("");

  const biasImages = [
    biasLeftImg,
    biasLeaningLeftImg,
    biasCenterImg,
    biasLeaningRightImg,
    biasRightImg,
  ];

  let data = {};
  data = metricCounts.map((metricCount, index) => ({
    label: labels[index],
    frequency: metricCount,
  }));

  useEffect(() => {
    const updateSize = () => {
      const breakpoint = 75 * 16;
      const variablesbreakpoint = 37.5 * 16;

      if (window.innerWidth <= breakpoint) {
        setSvgHeight("55rem");
      } else {
        setSvgHeight("45rem");
      }
      if (window.innerWidth <= variablesbreakpoint) {
        setSvgWidth("50rem");
        setSvgHeight("45rem");
      } else {
        setSvgWidth("80rem");
      }
    };
    window.addEventListener("resize", updateSize);
    updateSize();

    return () => window.removeEventListener("resize", updateSize);
  }, [window]);

  useEffect(() => {
    const handleResize = () => {
      setContainerWidth(d3Container.current.parentElement.offsetWidth);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (metricCounts.length === 0 || labels.length === 0) return;

    const svg = d3.select(d3Container.current);
    svg.selectAll("*").remove();

    let right = unratedMetric ? 105 : 30;

    if (containerWidth < 450) {
      if (unratedMetric) {
        right = 80;
      } else {
        right = 25;
      }
    }

    let left = unratedMetric ? 30 : 60;

    if (containerWidth < 450) {
      if (unratedMetric) {
        left = 15;
      } else {
        left = 42;
      }
    }

    const margin = {
      top: 20,
      right: right,
      bottom: 60,
      left: left,
    };
    const width = containerWidth - margin.left - margin.right;
    const height =
      containerWidth < 768
        ? 350 - margin.top - margin.bottom
        : Math.min(380, width) - margin.top - margin.bottom;

    let imageWidth = containerWidth < 768 ? 60 : 80;
    if (containerWidth < 450) {
      imageWidth = 50;
    }

    const xScale = d3.scaleBand().range([0, width]).domain(labels).padding(0.1);

    const yScale = d3
      .scaleLinear()
      .range([height, 0])
      .domain([0, maxMetricCount]);

    const xAxis = d3.axisBottom(xScale);

    const maxValue = maxMetricCount;

    let stepSize = maxValue / 9;

    let tickValues;
    if (maxValue < 10) {
      tickValues = d3.range(0, maxValue + 1);
    } else {
      tickValues = d3
        .range(0, maxValue + stepSize, stepSize)
        .map((value) => Math.min(value, maxValue));
    }

    const yAxis = d3
      .axisLeft(yScale)
      .tickValues(tickValues)
      .tickFormat(d3.format(".0f"));

    svg
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`)
      .call(yAxis)
      .selectAll(".tick text")
      .style("fill", "grey");

    const xAxisGroup = svg
      .append("g")
      .attr("transform", `translate(${margin.left}, ${height + margin.top})`)
      .call(xAxis);

    if (JSON.parse(metricFilter).value === 3) {
      xAxisGroup.selectAll(".tick text").style("opacity", 0);

      xAxisGroup
        .selectAll("image")
        .data(labels)
        .enter()
        .append("image")
        .attr("xlink:href", (label, i) => biasImages[i])
        .attr(
          "x",
          (label) => xScale(label) + xScale.bandwidth() / 2 - imageWidth / 2
        )
        .attr("y", -10)
        .attr("width", imageWidth)
        .attr("height", imageWidth);
    } else {
      xAxisGroup
        .selectAll("text")
        .text((d) => (d.length > 12 ? `${d.substring(0, 12)}...` : d))
        .attr("transform", "rotate(-30)")
        .style("text-anchor", "end")
        .attr("dx", "-.4em")
        .attr("dy", ".8em");
    }

    svg
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`)
      .call(yAxis);

    svg
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`)
      .selectAll("rect")
      .data(data)
      .enter()
      .append("rect")
      .on("mouseover", function (event, d) {
        const rect = d3Container.current.getBoundingClientRect();
        setTooltipTitle(d.label);
        setTooltipContent(`Metric Counts: ${d.frequency}`);
        setTooltipPosition({
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        });
        setTooltipVisibility(true);
      })
      .on("mouseout", function () {
        setTooltipVisibility(false);
      })
      .attr("x", (d) => xScale(d.label))
      .attr("y", (d) => yScale(d.frequency))
      .attr("width", xScale.bandwidth())
      .attr("height", (d) => height - yScale(d.frequency))
      .attr("fill", (d, i) => {
        if (JSON.parse(metricFilter).value === 3) {
          return colorArray[i % colorArray.length];
        } else {
          return "steelblue";
        }
      });
  }, [labels, metricCounts, metricFilter, containerWidth, sort]);

  useEffect(() => {
    const handleScroll = () => {
      if (tooltipVisibility && d3Container.current) {
        const containerElement = d3Container.current.parentElement;
        const containerRect = containerElement.getBoundingClientRect();
        const rect = d3Container.current.getBoundingClientRect();
        const scrollLeft = containerElement.scrollLeft;
        setTooltipPosition((prevPosition) => ({
          ...prevPosition,
          x:
            prevPosition.x +
            (containerRect.left - prevContainerLeft) +
            scrollLeft,
        }));

        prevContainerLeft = containerRect.left;
      }
    };

    let prevContainerLeft = 0;
    d3Container.current?.parentElement.addEventListener("scroll", handleScroll);

    return () => {
      d3Container.current?.parentElement.removeEventListener(
        "scroll",
        handleScroll
      );
      d3.select(d3Container.current).selectAll("svg").remove();
    };
  }, [labels, metricCounts, tooltipVisibility]);

  return (
    <div className="bar-chart-component">
      <div className="chart-header">
        <p></p>
        <p className="title-bar">My Information Diet</p>

        {JSON.parse(metricFilter).value === 0 && (
          <span
            className="sort"
            // onClick={() => {
            //   setSort(!sort);
            //   sortData();
            // }}
          >
            <MenuPrimary
              data={sortOptions.map((option) => ({
                ...option,
                onClick: () => {
                  sortData(option);
                  setSortVal(option);
                },
              }))}
              children={
                <div className="flex">
                  <p>{sortVal.name}</p>
                  {}
                  <KeyboardArrowDownOutlinedIcon />
                </div>
              }
            />
          </span>
        )}
      </div>
      <svg ref={d3Container} width={svgWidth} height={svgHeight} />
      <div
        style={{
          opacity: tooltipVisibility ? 1 : 0,
          left: `${tooltipPosition.x}px`,
          top: `${tooltipPosition.y}px`,
          position: "absolute",
        }}
        className="tooltip-box"
      >
        <p className="title">{tooltipTitle}</p>
        <div className="flex">
          <div className="color-box" />
          <p className="desc">{tooltipContent}</p>
        </div>
      </div>

      {unratedMetric ? (
        <div className="tooltip-unrated">
          <p className="title">Unrated</p>
          <div className="flex">
            <div className="color-box" />
            <p className="desc">{`Metric Counts ${unratedMetric}`}</p>
          </div>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default BarChartWithImages;
